import { Download } from '@mui/icons-material';
import { Grid } from '@mui/material';
import ExcelJS from 'exceljs';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getIncomeBookReport } from '../../../../../../lib/api/reports';
import CenteredCircularProgress from '../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions from '../../../../../../lib/designSystem/HeaderWithActions';
import { DateSearcher } from '../common/DateSearcher';
import ReportIncomeDailyTable from './DailyTable';
import IncomeBreakdownChart from './Dashboard/IncomeBreakdownChart';
import ProfitLossChart from './Dashboard/ProfitLossChart';
import ExecutiveDialog from './ExecutiveDialog';
import { exportIncomeBookToExcel } from './utils/exportToExcel';

// Helper functions to replace dayjs functionality
const getStartOfMonth = (date: Date = new Date()): Date => {
  date.setUTCDate(1);
  date.setUTCHours(0, 0, 0, 0);
  return date;
};

const getEndOfMonth = (date: Date = new Date()): Date => {
  date.setUTCDate(31);
  date.setUTCHours(23, 59, 59, 999);
  return date;
};

const isAfter = (date1: Date, date2: Date): boolean => {
  return date1.getTime() > date2.getTime();
};

const IncomeBook: React.FC = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const startDateDefault = getStartOfMonth();
  const [startDate, setStartDate] = useState(startDateDefault);
  const endDateDefault = getEndOfMonth();
  const [endDate, setEndDate] = useState(endDateDefault);
  const dispatch = useDispatch();
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [folio, setFolio] = useState('');
  const [loadingExport, setLoadingExport] = useState(false);
  const [dataWithLines, setDataWithLines] = useState([]);
  const [showExecutive, setShowExecutive] = useState(false);
  const [linesByExecutive, setLinesByExecutive] = useState([]);

  const generateQuery = ({
    startDateParam,
    endDateParam,
    showLinesParam,
  }: {
    startDateParam?: Date;
    endDateParam?: Date;
    showLinesParam?: boolean;
  } = {}) => {
    const startDateQuery = startDateParam || startDate;
    const endDateQuery = endDateParam || endDate;
    const showLinesQuery = showLinesParam || false;
    let query = '';
    if (startDateQuery) query += `startDate=${startDateQuery}&`;
    if (endDateQuery) query += `endDate=${endDateQuery}&`;
    if (businessIdentifier)
      query += `businessIdentifier=${businessIdentifier}&`;
    if (businessName) query += `businessName=${businessName}&`;
    if (folio) query += `folio=${folio}&`;
    query += `showLines=true&`;
    return query;
  };

  const exportToExcel = async () => {
    setLoadingExport(true);
    const startTitle = formatUTCDate(startDate);
    const endTitle = formatUTCDate(endDate);
    const fileName = `reporte_ingresos_${startTitle.replace(/\//g, '-')}_${endTitle.replace(/\//g, '-')}.xlsx`;

    await exportIncomeBookToExcel(data, fileName);
    setLoadingExport(false);
  };

  const validateDates = () => {
    console.log(startDate, endDate);
    if (!startDate || !endDate) {
      showAlert(
        dispatch,
        'warning',
        'Debe seleccionar una fecha de inicio y una fecha de fin',
      );
      return false;
    }
    if (isAfter(new Date(startDate), new Date(endDate))) {
      showAlert(
        dispatch,
        'warning',
        'La fecha de inicio no puede ser mayor a la fecha de fin',
      );
      return false;
    }
    return true;
  };

  const triggerSearch = async () => {
    setLoading(true);
    if (!validateDates()) {
      setLoading(false);
      return;
    }
    const query = generateQuery();
    const response = await getIncomeBookReport(dispatch, query);
    setData(response.linesByDate);
    setLinesByExecutive(response.linesByUserOwnerId);
    setLoading(false);
  };

  const setUp = async () => {
    try {
      setLoading(true);
      await triggerSearch();
      setLoading(false);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    setUp();
  }, []);

  const filters: Filter[] = [
    {
      label: 'Folio',
      type: 'text',
      value: folio,
      setter: setFolio,
    },
    {
      label: 'Fecha inicio',
      type: 'date',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      label: 'Fecha fin',
      type: 'date',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },
    {
      label: 'Nombre cliente',
      type: 'autocomplete',
      value: businessName,
      key: 'businessName',
      setter: setBusinessName,
    },
    {
      label: 'Rut cliente',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
  ];

  const formatUTCDate = (date: Date | string): string => {
    const utcDate = new Date(new Date(date).toUTCString());
    const day = utcDate.getUTCDate().toString().padStart(2, '0');
    const month = (utcDate.getUTCMonth() + 1).toString().padStart(2, '0');
    const year = utcDate.getUTCFullYear();
    return `${day}/${month}/${year}`;
  };

  const startTitle = formatUTCDate(startDate);
  const endTitle = formatUTCDate(endDate);

  if (loading) return <CenteredCircularProgress type="layout" />;
  return (
    <div>
      <Grid container justifyContent="left">
        <HeaderWithActions
          variant="body1"
          title={`Reporte de Ingresos ${startTitle} - ${endTitle}`}
          buttons={[
            {
              action: () => exportToExcel(),
              name: 'Descargar XSLX',
              color: 'primary',
              startIcon: <Download />,
              disabled: loadingExport,
            },
            {
              name: 'Buscar',
              action: () => {},
              color: 'primary',
              customButton: (
                <GlobalSearcher
                  filters={filters}
                  triggerSearch={triggerSearch}
                  dispatch={dispatch}
                  disableSearch={loading}
                />
              ),
            },
          ]}
        />
      </Grid>

      {/* Dashboard con gráficos */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <ProfitLossChart data={data} loading={loading} />
        <Grid item xs={12} md={4}>
          <IncomeBreakdownChart
            data={data}
            loading={loading}
            onClickExecutive={() => setShowExecutive(true)}
          />
        </Grid>
      </Grid>

      <ReportIncomeDailyTable
        rows={data}
        getIncomeBookReport={getIncomeBookReport}
        generateQuery={generateQuery}
      />
      {showExecutive && (
        <ExecutiveDialog
          linesByExecutive={linesByExecutive}
          startTitle={startTitle}
          endTitle={endTitle}
          openDialog={showExecutive}
          handleCloseDialog={() => setShowExecutive(false)}
        />
      )}
    </div>
  );
};

export default IncomeBook;
